import { Minus, Plus } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useInView } from "@/hooks/useInView";
import { FABCHAT_WHATSAPP_URL } from "@/lib/contact";
import { cn } from "@/lib/utils";

export const faqs = [
  {
    question: "Realmente leva só 5 minutos para configurar?",
    answer:
      "Sim. Depois de escolher seu plano, o cadastro leva cerca de 30 segundos. Em seguida você configura o agente em poucos minutos, define tom de voz e horários, e conecta o WhatsApp pela API oficial da Meta ou pelo QR Code. Sem código e sem complexidade. Muitas equipes começam a atender no mesmo dia.",
  },
  {
    question: "Preciso saber programar ou ter conhecimento técnico?",
    answer:
      "Não. A plataforma é visual e intuitiva. Tudo é configurado com formulários e cliques. Se você usa WhatsApp e sabe preencher um formulário, consegue criar seu agente de IA.",
  },
  {
    question: "O agente realmente conversa como humano?",
    answer:
      "Sim. A IA entende contexto, mantém tom de voz natural e adapta as respostas. Você define a personalidade, formal, casual ou técnica, e o agente mantém esse estilo. Muitos clientes nem percebem que estão falando com IA.",
  },
  {
    question: "Como funciona a conexão com WhatsApp?",
    answer:
      "Há duas formas. A API oficial (WhatsApp Cloud API da Meta) liga o número pela conta Business, com templates quando a Meta exige. A conexão por QR Code é mais rápida — como instalar o WhatsApp em outro aparelho — mas não é a API oficial e exige cuidado com volume e políticas do WhatsApp. Os dois caminhos entram no mesmo painel da FabChat.",
  },
  {
    question: "A FabChat dispara mensagens em massa?",
    answer:
      "Sim. O painel de disparos envia campanhas para leads do CRM, pela API oficial ou pela conexão QR, conforme o número ligado. Campanha em massa não é o mesmo que o agente de IA, que responde conversas com contexto.",
  },
  {
    question: "E se eu quiser que um humano assuma a conversa?",
    answer:
      "O agente identifica quando precisa transferir para um humano, em perguntas complexas ou cliente insatisfeito, por exemplo. Você recebe notificação e pode assumir a conversa a qualquer momento. O histórico completo fica salvo.",
  },
  {
    question: "Meus dados estão seguros?",
    answer:
      "Sim. Os dados são criptografados e seguimos a LGPD. Suas informações ficam isoladas e você pode exportar ou excluir tudo quando quiser.",
  },
  {
    question: "Posso cancelar quando quiser?",
    answer:
      "Sim. Sem fidelidade e sem burocracia. Você cancela com um clique. Se excluir a conta, seus dados são removidos imediatamente.",
  },
  {
    question: "Quantos agentes posso criar?",
    answer:
      "Depende do plano. Você pode criar agentes para vendas, suporte, agendamento e outros fluxos. Cada um com personalidade e instruções próprias, ideal para empresas com vários produtos ou equipes.",
  },
];

export const FAQ = () => {
  const { ref, isInView } = useInView({ threshold: 0.08 });

  return (
    <section
      id="faq"
      ref={ref}
      className="scroll-mt-24 border-t border-border/30 bg-muted/15 py-14 md:py-20"
    >
      <div className="container mx-auto max-w-6xl px-4">
        <div
          className={cn(
            "grid items-stretch gap-10 lg:grid-cols-[minmax(0,380px)_1fr] lg:gap-14 xl:gap-20",
            "transition-all duration-700",
            isInView ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
          )}
        >
          <div className="flex flex-col justify-between gap-10 lg:min-h-full lg:py-2">
            <h2 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-[2.75rem] lg:leading-[1.12] xl:text-5xl text-balance">
              Perguntas frequentes
            </h2>

            <div className="flex flex-col items-start gap-5 lg:gap-6">
              <p className="max-w-sm text-base leading-relaxed text-muted-foreground md:text-lg">
                Dúvidas sobre planos, configuração ou uso no dia a dia? Nossa equipe responde pelo
                WhatsApp com clareza.
              </p>
              <Button
                asChild
                className="h-11 rounded-full bg-primary px-8 text-sm font-semibold text-primary-foreground shadow-md shadow-primary/25 hover:bg-primary/90 md:h-12 md:px-10"
              >
                <a href={FABCHAT_WHATSAPP_URL} target="_blank" rel="noreferrer">
                  Falar com a equipe
                </a>
              </Button>
            </div>
          </div>

          <Accordion type="single" collapsible className="flex w-full flex-col gap-2">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="overflow-hidden rounded-xl border border-b-0 border-border/35 bg-card/80 data-[state=open]:border-primary/20"
              >
                <AccordionTrigger
                  className={cn(
                    "group flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left text-sm font-medium leading-snug text-foreground hover:no-underline md:px-5",
                    "[&>svg]:hidden",
                  )}
                >
                  <span className="min-w-0 flex-1 pr-2">{faq.question}</span>
                  <span
                    className={cn(
                      "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-colors duration-200",
                      "border-border/60 bg-transparent text-muted-foreground",
                      "group-data-[state=open]:border-primary/40 group-data-[state=open]:bg-primary/10 group-data-[state=open]:text-primary",
                    )}
                    aria-hidden
                  >
                    <Plus className="h-3.5 w-3.5 group-data-[state=open]:hidden" strokeWidth={2} />
                    <Minus className="hidden h-3.5 w-3.5 group-data-[state=open]:block" strokeWidth={2} />
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4 text-sm leading-relaxed text-muted-foreground md:px-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};
